// src/services/scribeEncounterService.js
// Encounter and recording management service for PrismScribe web
// Mirrors the extension's encounterScribeService patterns

import { apiService } from './api';
import { uploadSessionAudio } from './scribeUploadService';

/**
 * Build the ordered audio slot list for a recording row. A slot is one recorder
 * session's audio, stored EITHER as a Xano attachment (legacy `audio_segments`)
 * OR as a GCS object (`audio_objects`, direct upload). A row never mixes the two.
 */
function buildAudioSlots(row) {
  const slots = [];
  const attachments = Array.isArray(row?.audio_segments) && row.audio_segments.length > 0
    ? row.audio_segments
    : (row?.audio_file ? [row.audio_file] : []);
  attachments.forEach((meta, index) => {
    if (meta) slots[index] = { kind: 'attachment', index, meta };
  });
  (Array.isArray(row?.audio_objects) ? row.audio_objects : []).forEach((object, position) => {
    if (!object) return;
    const index = Number.isInteger(object.segment_index) ? object.segment_index : position;
    slots[index] = { kind: 'object', index, object };
  });
  // Dense, ordered list (defensive against gaps)
  return slots.filter(Boolean).sort((a, b) => a.index - b.index);
}

class ScribeEncounterService {
  constructor() {
    this.currentEncounter = null;
    this.currentRecording = null;
    this.recordings = [];
    this.aggregatedTranscript = '';
    this.selectedTemplate = null;
  }

  /**
   * Initialize for a specific encounter
   */
  async initializeForEncounter(encounterId) {
    try {
      // Reset state
      this.currentRecording = null;
      
      // Load encounter details
      this.currentEncounter = await this.loadEncounterDetails(encounterId);
      
      // Load existing recordings for this encounter
      this.recordings = await this.loadEncounterRecordings(encounterId);
      
      // Build aggregated transcript if recordings exist
      if (this.recordings.length > 0) {
        this.rebuildAggregatedTranscript();
      }

      return {
        encounter: this.currentEncounter,
        recordings: this.recordings,
        hasRecordings: this.recordings.length > 0
      };
    } catch (error) {
      console.error('[ScribeEncounter] Failed to initialize:', error);
      throw error;
    }
  }

  /**
   * Load encounter details from API
   */
  async loadEncounterDetails(encounterId) {
    const response = await apiService.getEncounter(encounterId);
    
    return {
      id: response.id,
      patientName: `${response.patient?.first_name || ''} ${response.patient?.last_name || ''}`.trim(),
      patientId: response.patient_id,
      patient: response.patient,
      encounterDate: response.encounter_date,
      status: response.status,
      templateId: response.template_id,
      generatedDocumentation: response.generated_documentation
    };
  }

  /**
   * Load recordings for this encounter and set up currentRecording
   */
  async loadEncounterRecordings(encounterId) {
    try {
      const response = await apiService.getEncounterRecordings(encounterId);
      const recordings = response || [];

      // If we have a recording, set it as current (like extension does)
      if (recordings.length > 0) {
        const recording = recordings[0];

        // Server-side audio files: audio_segments is authoritative when
        // non-empty; legacy rows have only audio_file (treated as segment 0).
        // Audio is never downloaded eagerly and never concatenated locally —
        // a new recorder session appends its own segment file.
        const audioSegments = (recording.audio_segments?.length > 0)
          ? recording.audio_segments
          : (recording.audio_file ? [recording.audio_file] : []);
        const audioObjects = Array.isArray(recording.audio_objects) ? recording.audio_objects : [];
        const audioSlots = buildAudioSlots(recording);

        this.currentRecording = {
          id: `rec_${encounterId}`,
          encounterId: encounterId,
          backendId: recording.id,
          segments: recording.segments || [],
          status: 'stopped',
          transcript: recording.transcript || '',
          transcriptionStatus: recording.transcription_status,
          transcriptionError: recording.transcription_error || null,
          duration: recording.duration || 0,
          audioMetadata: recording.audio_file || null,
          audioSegments,
          audioObjects,
          audioSlots,
          sessionBlob: null,
          sessionSegmentIndex: audioSlots.length,
          dirty: false
        };
      } else {
        this.currentRecording = null;
      }

      return recordings;
    } catch (error) {
      console.error('[ScribeEncounter] Failed to load recordings:', error);
      return [];
    }
  }

  /**
   * Download recording audio from backend
   */
  async downloadRecordingAudio(recording) {
    const audioMetadata = recording.audio_file || recording.audioMetadata;
    
    if (!audioMetadata) {
      throw new Error('No audio file available for this recording');
    }

    try {
      let audioUrl;

      if (audioMetadata.url) {
        // Direct URL available (demo environment)
        audioUrl = audioMetadata.url;
      } else if (audioMetadata.path) {
        // Need to generate download URL (production)
        audioUrl = await apiService.getAttachmentDownloadUrl(audioMetadata.path);
      } else {
        throw new Error('No audio path or URL available');
      }

      // Fetch the audio file
      const response = await fetch(audioUrl);
      if (!response.ok) {
        throw new Error(`Failed to download audio: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('[ScribeEncounter] Error downloading audio:', error);
      throw error;
    }
  }

  /**
   * Ensure a currentRecording object exists for the loaded encounter.
   */
  ensureRecording() {
    if (!this.currentEncounter) {
      throw new Error('No encounter loaded');
    }

    if (!this.currentRecording) {
      this.currentRecording = {
        id: `rec_${this.currentEncounter.id}`,
        encounterId: this.currentEncounter.id,
        backendId: null,
        startTime: Date.now(),
        segments: [],
        status: 'ready',
        transcript: '',
        audioSegments: [],
        audioObjects: [],
        audioSlots: [],
        sessionBlob: null,
        sessionSegmentIndex: 0,
        dirty: false
      };
    }

    return this.currentRecording;
  }

  /**
   * Which storage transport this recording row uses for audio:
   * 'object' (GCS direct upload), 'attachment' (legacy Xano upload), or null
   * when the row has no audio yet. Transport is sticky per row.
   */
  getAudioTransport() {
    const recording = this.currentRecording;
    if (!recording) return null;
    if (recording.audioObjects?.length > 0) return 'object';
    if (recording.audioSegments?.length > 0 || recording.audioMetadata) return 'attachment';
    return null;
  }

  /**
   * Open a new timing segment (pause/resume bookkeeping for duration display).
   * Timing segments are metadata only — NOT 1:1 with audio files.
   */
  openTimingSegment() {
    const recording = this.ensureRecording();

    recording.segments.push({
      startTime: Date.now(),
      endTime: null
    });

    recording.status = 'recording';
  }

  /**
   * Begin a new recorder session. One recorder session = one server audio
   * file slot; the session's cumulative blob re-uploads into the same slot
   * on every pause, and a NEW session (cross-browser-session append, iOS
   * forced restart, file upload) claims the next slot.
   */
  beginNewRecorderSession() {
    const recording = this.ensureRecording();

    // Never silently discard unsaved audio — callers must retry runSave()
    // (and surface the failure) before starting a new session.
    if (recording.dirty && recording.sessionBlob) {
      throw new Error('Previous audio segment has not been saved yet — check your connection and try again');
    }

    recording.sessionBlob = null;
    recording.dirty = false;
    recording.sessionSegmentIndex = recording.audioSlots?.length ?? recording.audioSegments.length;
  }

  /**
   * Create a new recording or prepare for appending.
   * Starts a fresh recorder session and opens a timing segment.
   */
  prepareRecording() {
    const recording = this.ensureRecording();
    const isAppending = !!recording.backendId;

    this.beginNewRecorderSession();
    this.openTimingSegment();

    return {
      recordingId: recording.id,
      isAppending: isAppending,
      segmentCount: recording.segments.length
    };
  }

  /**
   * Persist the current recorder session's cumulative audio blob.
   * Replaces the old concat-and-save flow: the blob is a valid single-container
   * file (chunks from one MediaRecorder session) and uploads into this
   * session's segment slot. Never concatenates blobs across sessions.
   */
  async persistSessionAudio(newAudioBlob, { closeTimingSegment = true } = {}) {
    if (!this.currentRecording) {
      console.error('[ScribeEncounter] No current recording to save');
      return { success: false, error: 'No recording in progress' };
    }

    try {
      if (closeTimingSegment) {
        const currentSegment = this.currentRecording.segments[this.currentRecording.segments.length - 1];
        if (currentSegment && !currentSegment.endTime) {
          currentSegment.endTime = Date.now();
        }
      }

      this.currentRecording.sessionBlob = newAudioBlob;
      this.currentRecording.dirty = true;
      this.currentRecording.status = 'stopped';

      const savedRecording = await this.runSave();

      this.currentRecording.duration = this.getTotalRecordingDuration();

      return {
        success: true,
        recording: savedRecording,
        totalDuration: this.currentRecording.duration,
        canResume: true
      };
    } catch (error) {
      console.error('[ScribeEncounter] Error persisting session audio:', error);
      this.currentRecording.status = 'error';
      return {
        success: false,
        error: error.message,
        totalDuration: this.getTotalRecordingDuration()
      };
    }
  }

  /**
   * Deprecated alias — older callers (ScribeEncounterDetails) hand a finished
   * blob here. Delegates to persistSessionAudio.
   */
  async handleRecordingStopped(newAudioBlob) {
    return this.persistSessionAudio(newAudioBlob, { closeTimingSegment: true });
  }

  /**
   * Append an uploaded audio file as its own segment. Uploaded files are
   * complete containers from another device/app — they are never merged with
   * in-memory session state.
   */
  async appendUploadedAudio(audioBlob, estimatedDuration = 0) {
    this.ensureRecording();

    const now = Date.now();
    this.currentRecording.segments.push({
      startTime: now - (estimatedDuration || 0),
      endTime: now
    });

    this.beginNewRecorderSession();

    return this.persistSessionAudio(audioBlob, { closeTimingSegment: false });
  }

  /**
   * Serialized save entry point: one save in flight at a time, with a single
   * coalesced trailing save for requests that arrive mid-upload. The trailing
   * save always reads the latest session state, so a pause→stop race can never
   * upload stale audio or double-POST.
   */
  async runSave() {
    if (this._queuedSave) return this._queuedSave;

    if (this._activeSave) {
      this._queuedSave = this._activeSave
        .catch(() => {})
        .then(() => {
          this._queuedSave = null;
          return this.runSave();
        });
      return this._queuedSave;
    }

    this._activeSave = (async () => {
      try {
        return await this._executeSave();
      } finally {
        this._activeSave = null;
      }
    })();

    return this._activeSave;
  }

  /**
   * Save recording to backend (create or update). Internal — call runSave().
   */
  async _executeSave() {
    const recording = this.currentRecording;
    if (!recording) {
      throw new Error('No recording to save');
    }

    const blobAtSave = recording.sessionBlob;
    const hasDirtyAudio = recording.dirty && !!blobAtSave;
    const transport = this.getAudioTransport();

    let savedRecording;

    if (hasDirtyAudio && transport !== 'attachment') {
      // Direct upload (browser → GCS). A row that already stores objects must
      // never fall back to attachments (mixed rows cannot be transcribed); a
      // row with no audio yet may fall back so rollout is order-independent.
      try {
        savedRecording = await this._saveViaObject(recording, blobAtSave);
      } catch (error) {
        if (transport === 'object') throw error;
        console.warn('[ScribeEncounter] Direct upload unavailable, falling back to attachment upload:', error?.message);
        savedRecording = await this._saveViaMultipart(recording, blobAtSave);
      }
    } else {
      savedRecording = await this._saveViaMultipart(recording, hasDirtyAudio ? blobAtSave : null);
    }

    if (!recording.backendId && savedRecording?.id) {
      recording.backendId = savedRecording.id;
    }

    if (hasDirtyAudio) {
      // A newer blob may have replaced sessionBlob mid-upload (fast
      // pause→resume→pause) — only clear dirty if we saved the latest.
      if (recording.sessionBlob === blobAtSave) {
        recording.dirty = false;
      }
    }

    this._mirrorSavedRow(recording, savedRecording);

    return savedRecording;
  }

  /**
   * Legacy transport: multipart to POST /recordings or PATCH /recording/{id}
   * with the audio blob (Xano stores it as an attachment).
   */
  async _saveViaMultipart(recording, blobAtSave) {
    const formData = new FormData();
    formData.append('encounter_id', recording.encounterId);
    formData.append('transcript', recording.transcript || '');
    formData.append('duration', String(this.getTotalRecordingDuration()));
    formData.append('segments', JSON.stringify(recording.segments));

    if (blobAtSave) {
      const mimeType = blobAtSave.type || 'audio/webm';
      const extension = mimeType.includes('mp4') || mimeType.includes('m4a') ? 'mp4' : 'webm';
      formData.append('audio', blobAtSave, `recording_${recording.id}.${extension}`);

      if (recording.backendId) {
        formData.append('segment_index', String(recording.sessionSegmentIndex));
      }
    }

    if (recording.backendId) {
      return apiService.updateRecording(recording.backendId, formData);
    }

    try {
      return await apiService.saveRecording(formData);
    } catch (error) {
      // POST-ambiguity guard: the server may have created the row even
      // though the client saw a failure. Adopt the existing row so the
      // retry PATCHes instead of creating a duplicate recording.
      const adoptedId = await this.findExistingRecordingId(recording.encounterId);
      if (!adoptedId) throw error;

      recording.backendId = adoptedId;
      return apiService.updateRecording(adoptedId, formData);
    }
  }

  /**
   * Direct transport: (1) make sure the row exists (metadata-only JSON POST),
   * (2) PUT the blob to GCS through a Xano-minted session, (3) PATCH the row
   * with the object descriptor for this session's slot.
   */
  async _saveViaObject(recording, blobAtSave) {
    const metadata = {
      encounter_id: recording.encounterId,
      transcript: recording.transcript || '',
      duration: this.getTotalRecordingDuration(),
      segments: recording.segments,
    };

    if (!recording.backendId) {
      let created;
      try {
        created = await apiService.saveRecording(JSON.stringify(metadata));
      } catch (error) {
        const adoptedId = await this.findExistingRecordingId(recording.encounterId);
        if (!adoptedId) throw error;
        created = { id: adoptedId };
      }
      if (!created?.id) throw new Error('Recording row could not be created');
      recording.backendId = created.id;
    }

    const audioObject = await uploadSessionAudio(blobAtSave, {
      encounterId: recording.encounterId,
      recordingId: recording.backendId,
      segmentIndex: recording.sessionSegmentIndex,
    });

    return apiService.updateRecording(recording.backendId, JSON.stringify({
      ...metadata,
      audio_object: audioObject,
      segment_index: recording.sessionSegmentIndex,
    }));
  }

  /**
   * Mirror the server's audio state onto the in-memory recording.
   */
  _mirrorSavedRow(recording, savedRecording) {
    if (!savedRecording) return;

    if (Array.isArray(savedRecording.audio_segments) && savedRecording.audio_segments.length > 0) {
      recording.audioSegments = savedRecording.audio_segments;
    } else if (savedRecording.audio_file && recording.audioSegments.length === 0) {
      recording.audioSegments = [savedRecording.audio_file];
    }

    if (Array.isArray(savedRecording.audio_objects)) {
      recording.audioObjects = savedRecording.audio_objects;
    }

    recording.audioSlots = buildAudioSlots({
      audio_segments: recording.audioSegments,
      audio_file: recording.audioMetadata,
      audio_objects: recording.audioObjects,
    });
  }

  /**
   * Legacy-name wrapper kept for external callers; serialized like all saves.
   */
  async saveRecording() {
    return this.runSave();
  }

  /**
   * Look up an existing recording row for this encounter (POST-ambiguity guard).
   */
  async findExistingRecordingId(encounterId) {
    try {
      const response = await apiService.getEncounterRecordings(encounterId);
      return response?.[0]?.id || null;
    } catch {
      return null;
    }
  }

  /**
   * Calculate total recording duration across all segments
   */
  getTotalRecordingDuration() {
    if (!this.currentRecording?.segments) return 0;

    return this.currentRecording.segments.reduce((total, segment) => {
      const endTime = segment.endTime || Date.now();
      const segmentDuration = endTime - segment.startTime;
      return total + segmentDuration;
    }, 0);
  }

  /**
   * Whether any audio exists for the current recording (server files,
   * in-memory session blob, or legacy metadata).
   */
  hasAudio() {
    const recording = this.currentRecording;
    if (!recording) return false;

    return (recording.audioSlots?.length > 0)
      || (recording.audioSegments?.length > 0)
      || (recording.audioObjects?.length > 0)
      || !!recording.sessionBlob
      || !!recording.audioMetadata;
  }

  /**
   * Get current recording status for UI
   */
  getRecordingStatus() {
    if (!this.currentRecording) return null;

    return {
      status: this.currentRecording.status,
      hasAudio: this.hasAudio(),
      segments: this.currentRecording.segments?.length || 0,
      totalDuration: this.getTotalRecordingDuration(),
      backendId: this.currentRecording.backendId,
      transcriptionStatus: this.currentRecording.transcriptionStatus,
      transcriptionError: this.currentRecording.transcriptionError || null
    };
  }

  /**
   * Download a single attachment (segment file) as a blob.
   */
  async downloadAttachmentBlob(attachmentMeta) {
    if (!attachmentMeta) {
      throw new Error('No audio file available');
    }

    let audioUrl;
    if (attachmentMeta.url) {
      audioUrl = attachmentMeta.url;
    } else if (attachmentMeta.path) {
      audioUrl = await apiService.getAttachmentDownloadUrl(attachmentMeta.path);
    } else {
      throw new Error('No audio path or URL available');
    }

    const response = await fetch(audioUrl);
    if (!response.ok) {
      throw new Error(`Failed to download audio: ${response.status}`);
    }

    return await response.blob();
  }

  /**
   * Collect the ordered audio parts for transcription — one blob per recorder
   * session. The current session's in-memory blob is used directly; other
   * segments are downloaded from the backend.
   */
  async getSessionAudioParts() {
    const recording = this.currentRecording;
    if (!recording) return [];

    if (this.getAudioTransport() === 'object') {
      // Direct-upload rows are transcribed from their gs:// objects; the
      // client never re-downloads or re-encodes them.
      throw new Error('This recording is stored in cloud storage; transcribe it by recording id');
    }

    const parts = [];
    const segmentCount = recording.audioSegments?.length || 0;

    for (let i = 0; i < segmentCount; i++) {
      if (i === recording.sessionSegmentIndex && recording.sessionBlob) {
        parts.push(recording.sessionBlob);
      } else {
        parts.push(await this.downloadAttachmentBlob(recording.audioSegments[i]));
      }
    }

    // Current session not yet mirrored into audioSegments (save failed /
    // offline) — still transcribable from memory.
    if (recording.sessionBlob && recording.sessionSegmentIndex >= segmentCount) {
      parts.push(recording.sessionBlob);
    }

    return parts;
  }

  /**
   * Rebuild aggregated transcript from recordings
   */
  rebuildAggregatedTranscript() {
    this.aggregatedTranscript = this.recordings
      .filter(rec => rec.transcript)
      .map(rec => rec.transcript)
      .join('\n\n--- Segment Break ---\n\n');
  }

  /**
   * Get transcript
   */
  getTranscript() {
    return this.aggregatedTranscript;
  }

  // ============ Encounter CRUD ============

  /**
   * Create a new scribe encounter
   */
  async createEncounter(data) {
    try {
      const encounterData = {
        patient_id: data.patientId,
        provider_id: data.providerId,
        organization_id: data.organizationId,
        location_id: data.locationId || null,
        encounter_date: Date.now(),
        encounter_type: 'scribe',
        status: 'in_progress',
        documentation_status: 'pending',
        template_id: data.templateId || null
      };

      const encounter = await apiService.createEncounter(encounterData);
      
      return { success: true, encounter };
    } catch (error) {
      console.error('[ScribeEncounter] Failed to create encounter:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get encounter by ID
   */
  async getEncounter(encounterId) {
    try {
      const result = await this.initializeForEncounter(encounterId);
      return { 
        success: true, 
        encounter: this.currentEncounter,
        recordings: this.recordings
      };
    } catch (error) {
      console.error('[ScribeEncounter] Failed to get encounter:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update encounter
   */
  async updateEncounter(encounterId, updates) {
    try {
      const encounter = await apiService.updateEncounter(encounterId, updates);
      
      if (this.currentEncounter?.id === encounterId) {
        this.currentEncounter = { ...this.currentEncounter, ...updates };
      }
      
      return { success: true, encounter };
    } catch (error) {
      console.error('[ScribeEncounter] Failed to update encounter:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get encounters list for provider
   * @param {string|null} providerId - Provider ID to filter by, or null for all org encounters
   * @param {string} organizationId
   * @param {Object} options
   */
  async getEncountersList(providerId, organizationId, options = {}) {
    try {
      const params = {
        organization_id: organizationId,
        encounter_type: 'scribe',
        ...options
      };

      if (providerId) {
        params.provider_id = providerId;
      }

      const response = await apiService.getEncounters(params);
      return { success: true, encounters: response };
    } catch (error) {
      console.error('[ScribeEncounter] Failed to get encounters list:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete encounter
   */
  async deleteEncounter(encounterId) {
    try {
      await apiService.deleteEncounter(encounterId);
      
      if (this.currentEncounter?.id === encounterId) {
        this.reset();
      }

      return { success: true };
    } catch (error) {
      console.error('[ScribeEncounter] Failed to delete encounter:', error);
      return { success: false, error: error.message };
    }
  }

  // ============ Transcription & Documentation ============

  /**
   * Convert blob to base64 string (matching extension pattern)
   */
  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Trigger transcription and documentation generation
   * Sends audio blob as base64 (matching extension's aiService.transcribeAudio).
   * Direct-upload rows (audio_objects) carry no audio: the backend reads the
   * GCS object by recording id.
   */
  async transcribeAndGenerate(templateId, options = {}) {
    if (!this.currentRecording?.backendId) {
      return { success: false, error: 'No recording to transcribe' };
    }

    try {
      const isObjectRow = this.getAudioTransport() === 'object';

      const payload = {
        recording_id: this.currentRecording.backendId,
        encounter_id: this.currentEncounter.id,
        template_id: templateId,
        existing_field_values: options.existingFieldValues || {},
        existing_profile: options.existingProfile || null
      };

      if (!isObjectRow) {
        // Get audio blob — the current session's blob, or the first segment
        // file from the backend. NOTE: this development pipeline endpoint
        // accepts a single audio file; multi-segment recordings are transcribed
        // from their first segment only (the transcribe-ehr pipeline handles
        // multi-segment fully).
        let audioBlob = this.currentRecording.sessionBlob;

        if (!audioBlob && this.currentRecording.audioSegments?.length > 0) {
          audioBlob = await this.downloadAttachmentBlob(this.currentRecording.audioSegments[0]);
          if (this.currentRecording.audioSegments.length > 1) {
            console.warn('[ScribeEncounter] Multi-segment recording on development pipeline — transcribing first segment only');
          }
        }

        if (!audioBlob && this.currentRecording.audioMetadata) {
          audioBlob = await this.downloadRecordingAudio(this.currentRecording);
        }

        if (!audioBlob) {
          return { success: false, error: 'No audio available for transcription' };
        }

        // Convert audio to base64 (matching extension pattern)
        const base64Audio = await this.blobToBase64(audioBlob);

        // Determine file extension from blob type
        const mimeType = audioBlob.type || 'audio/webm';
        const extension = mimeType.includes('mp4') || mimeType.includes('m4a') ? 'm4a' : 'webm';

        payload.audio = base64Audio;
        payload.filename = `recording_${this.currentRecording.backendId}.${extension}`;
        payload.model = 'whisper-1';
        payload.language = options.language || 'en';
      }

      // Send request matching extension's aiService.transcribeAudio format
      const result = await apiService.transcribeAndGenerate(payload);

      // Update local status
      if (this.currentRecording) {
        this.currentRecording.transcriptionStatus = 'processing';
      }

      return { success: true, ...result };
    } catch (error) {
      console.error('[ScribeEncounter] Failed to start transcription:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save documentation to encounter
   */
  async saveDocumentation(encounterId, documentation) {
    try {
      const result = await apiService.updateEncounter(encounterId, {
        generated_documentation: documentation
      });

      return { success: true, encounter: result };
    } catch (error) {
      console.error('[ScribeEncounter] Failed to save documentation:', error);
      return { success: false, error: error.message };
    }
  }

  // ============ Template ============

  /**
   * Select template for documentation generation
   */
  async selectTemplate(templateId, templates) {
    const template = templates?.find(t => t.id === templateId);
    
    if (template) {
      this.selectedTemplate = template;
      
      // Save template_id to encounter
      if (this.currentEncounter) {
        try {
          await apiService.updateEncounter(this.currentEncounter.id, {
            template_id: templateId
          });
        } catch (error) {
          console.error('[ScribeEncounter] Failed to save template to encounter:', error);
        }
      }
    }
    
    return template;
  }

  // ============ Utilities ============

  /**
   * Clear current recording
   */
  clearCurrentRecording() {
    this.currentRecording = null;
  }

  /**
   * Reset service state
   */
  reset() {
    this.currentEncounter = null;
    this.currentRecording = null;
    this.recordings = [];
    this.aggregatedTranscript = '';
    this.selectedTemplate = null;
  }
}

export const scribeEncounterService = new ScribeEncounterService();